import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { predictionVoucherRuleApi } from '../api';
import type { PredictionVoucherBundle, PredictionVoucherRulePayload } from '../types';
import { getDefaultBundle, getErrorMessage } from '../utils';

export function usePredictionVoucherRule() {
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<PredictionVoucherBundle>(getDefaultBundle());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const result = await predictionVoucherRuleApi.getRule(controller.signal);

      if (!mountedRef.current) return;

      setData(result);
    } catch (err) {
      if (mountedRef.current) {
        setError(new Error(getErrorMessage(err)));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const replaceRule = useCallback(async (payload: PredictionVoucherRulePayload) => {
    setSaving(true);

    try {
      return await predictionVoucherRuleApi.replaceRule(payload);
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    fetchData();

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, [fetchData]);

  return useMemo(
    () => ({
      ...data,
      loading,
      saving,
      error,
      refetch: fetchData,
      replaceRule,
    }),
    [data, error, fetchData, loading, replaceRule, saving]
  );
}
